import {
  Button,
  Card,
  Divider,
  Flex,
  InputNumber,
  Layout,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { Check, FileDown, Redo, X } from "lucide-react";
import { useExamStore } from "@store";
import { chooseQuestionTypeLayout } from "./helpers";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  useGetExamInstanceQuestion,
  useGetGradingConfigurations,
  useUpdateExamInstancePoints,
  useGetGradingSummary,
} from "@hooks";
import { ExamInstanceQuestionState, ExamInstanceTree } from "@types";
import parse from "html-react-parser";
import { getBaseURL } from "../../../context/helpers";
import { ExamInstanceStatus } from "../../grading/ExamInstanceStatus";
import { GradingStatusEnum } from "../../../types/grading";

interface Props {
  examInstanceId?: string;
  examInstanceTree?: ExamInstanceTree;
  /** Grading configuration ID - if set, enables D-Level grading UI */
  gradingConfigurationId?: string;
}
export const ExamContent = ({ examInstanceId, examInstanceTree, gradingConfigurationId }: Props) => {
  const currentQuestion = useExamStore((state) => {
    return examInstanceTree?.groups[state.clickedGroup].questions[
      state.clickedQuestionIndex
    ];
  });

  const currentIndex = useExamStore((state) => state.clickedQuestionIndex);
  const currentGroup = useExamStore((state) => state.clickedGroup);


  const setStatus = useExamStore((state) => state.setStatus);
  const setPoints = useExamStore((state) => state.setPoints);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const nextQuestion = useExamStore((state) => state.nextQuestion);
  const previousQuestion = useExamStore((state) => state.previousQuestion);
  const updateExamInstancePoints = useUpdateExamInstancePoints();
  const { data, isLoading } = useGetExamInstanceQuestion(currentQuestion?.id);
  const { data: gradingConfigurations } = useGetGradingConfigurations();

  // Fetch grading summary if D-Level grading is enabled
  const { data: gradingSummary } = useGetGradingSummary(
    examInstanceId,
    { enabled: !!gradingConfigurationId }
  );

  const gradingConfiguration = gradingConfigurations?.find(
    (config) => config.id === gradingConfigurationId
  );
  const manualScoreMin = gradingConfiguration?.rules?.manualScoreMin ?? data?.unansweredPoints ?? 0;
  const manualScoreMax = gradingConfiguration?.rules?.manualScoreMax ?? data?.correctAnswerPoints ?? 0;
  const manualScoreStep = gradingConfiguration?.rules?.manualScoreStep ?? 0.5;

  const [questionStatus, setQuestionStatus] = useState<number | undefined>(
    data?.answerState
  );
  const [questionPoints, setQuestionPoints] = useState<number | undefined>(
    data?.points
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (data) {
      setQuestionStatus(data.answerState);
      setQuestionPoints(data.points);
      setIsEditing(false)
    }
  }, [data]);

  return (
    <Layout>
      <Card
      
        title={`${currentGroup + 1}. ${
          examInstanceTree?.groups[currentGroup]?.name
        }`}
        className="custom-card"
        style={{ marginBottom: 30 }}
      >
        <Space style={{ display: "flex", alignItems: "center" }}>
          <Typography.Text>{t("pages.examPeriods.points")}:</Typography.Text>
          <Check style={{ color: "green" }} />
          <Typography.Text>{data?.correctAnswerPoints}</Typography.Text>
          <X style={{ color: "red" }} />
          <Typography.Text>{data?.incorrectAnswerPoints}</Typography.Text>
          <Redo />
          <Typography.Text>{data?.unansweredPoints}</Typography.Text>
        </Space>

        <div key={`${data?.id}-${data?.text}`}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space direction="vertical">
              <Typography.Title level={5}>
                {t("pages.examPeriods.question")} {currentIndex + 1}:
              </Typography.Title>
              <Typography.Text>
                {parse(currentQuestion?.text || "")}
              </Typography.Text>
              {data?.attachments?.map((attachment: any) => (
                <Typography.Link
                  key={attachment.id}
                  href={`${getBaseURL()}${attachment.path}`}
                  target="_blank"
                >
                  <FileDown /> {attachment.name}
                </Typography.Link>
              ))}
            </Space>
          </motion.div>
        </div>
      </Card>
      {isLoading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ display: "flex", justifyContent: "center" }}
          exit={{ opacity: 0 }}
        >
          <Spin key={"spin"} />
        </motion.div>
      ) : (
        <motion.div
          key={"layout"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {chooseQuestionTypeLayout(data, {
            onApplyAISuggestion: (suggestedPoints: number) => {
              // Cap at max points for this question
              const maxPoints = data?.correctAnswerPoints ?? suggestedPoints;
              const cappedPoints = Math.min(suggestedPoints, maxPoints);
              setQuestionPoints(cappedPoints);
              setPoints(cappedPoints);
              // Enable editing mode so user can save or adjust
              setIsEditing(true);
            }
          })}
        </motion.div>
      )}
      <div>
        <Divider />
        <Typography.Title level={4}>
          {t("pages.examPeriods.assignPoints")}
        </Typography.Title>
        <Space>
          <Select
            disabled={!isEditing}
            value={questionStatus}
            style={{ width: 200 }}
            onChange={(value) => {
              setQuestionStatus(value);
              setStatus(value);
            }}
            options={[
              {
                value: ExamInstanceQuestionState.Correct,
                label: (
                  <Typography.Text strong type="success">
                    {t("pages.examPeriods.correctAnswer")}
                  </Typography.Text>
                ),
              },
              {
                value: ExamInstanceQuestionState.Incorrect,
                label: (
                  <Typography.Text strong type="danger">
                    {t("pages.examPeriods.incorrectAnswer")}
                  </Typography.Text>
                ),
              },
              {
                value: ExamInstanceQuestionState.Unanswered,
                label: (
                  <Typography.Text strong type="warning">
                    {t("pages.examPeriods.noAnswer")}
                  </Typography.Text>
                ),
              },
              {
                value: ExamInstanceQuestionState.RequiresManualGrading,
                label: (
                  <Typography.Text strong style={{ color: '#722ed1' }}>
                    {t("pages.examPeriods.requiresManualGrading")}
                  </Typography.Text>
                ),
              },
            ]}
          />
          <InputNumber
            disabled={!isEditing}
            min={manualScoreMin}
            max={manualScoreMax}
            step={manualScoreStep}
            precision={2}
            value={questionPoints}
            onChange={(val: number | null) => {
              setQuestionPoints(val as number);
              setPoints(val);
            }}
            prefix={
              <Typography.Text strong>
                {t("pages.examPeriods.points")}:
              </Typography.Text>
            }
            style={{ width: 120 }}
          />
          <Button
            type="primary"
            loading={updateExamInstancePoints.isPending}
            onClick={() => {
              if (isEditing && currentQuestion?.id) {
                updateExamInstancePoints.mutate({
                  answerState: questionStatus,
                  score: questionPoints,
                  examInstanceId: examInstanceId,
                  id: currentQuestion.id,
                  // Pass gradingConfigurationId to trigger D-Level grading logic if applicable
                  gradingConfigurationId: gradingConfigurationId,
                });
              }
              setIsEditing(!isEditing);
            }}
          >
            {isEditing
              ? t("pages.examPeriods.save")
              : t("pages.examPeriods.edit")}
          </Button>
        </Space>
      </div>
      <Flex gap={"small"} style={{ marginTop: 15 }}>
        <Button
          block
          type="dashed"
          disabled={currentGroup === 0 && currentIndex === 0}
          onClick={() => {
            previousQuestion();
          }}
        >
          {t("pages.examPeriods.prev")}
        </Button>
        <Button
          block
          disabled={
            currentGroup === examInstanceTree?.groups.length! - 1 &&
            currentIndex ===
              examInstanceTree?.groups[currentGroup].questions.length! - 1
          }
          onClick={() => {
            nextQuestion();
          }}
        >
          {t("pages.examPeriods.next")}
        </Button>
      </Flex>

      {/* D-Level Grading Status Display */}
      {gradingConfigurationId && gradingSummary && (
        <Card style={{ marginTop: 16 }} size="small">
          <ExamInstanceStatus
            examInstanceId={examInstanceId || ''}
            gradingStatus={gradingSummary.gradingStatus as GradingStatusEnum}
            totalCompetencies={gradingSummary.totalCompetencies}
            passedCompetencies={gradingSummary.passedCompetencies}
            hasPassed={gradingSummary.hasPassed}
            passPercentage={gradingConfiguration?.rules?.examPassPercentage}
            showDetails={true}
          />
        </Card>
      )}
    </Layout>
  );
};
